// F11.1 — Chunked file send/receive state machines.
//
// Sender state machine:
//   1. Emit file-offer
//   2. Await accept (caller signals via the returned `.acknowledgeAccept()`)
//   3. Loop: slice 16KB → wait backpressure → send file-chunk
//   4. Emit file-eof with CRC32
//
// Receiver state machine:
//   - feed(env) routes envelopes by kind; accumulates chunks; verifies CRC
//     on eof; surfaces complete blob to onComplete.
//
// We deliberately do NOT own the DataChannel here — the caller passes one in.
// This keeps fileTransfer free of WebRTC details and trivially testable with
// a vi.fn() channel mock.

import { Crc32Stream } from './crc32.js';
import { serialize } from './envelope.js';
import { LIMITS, maxBytesFor } from './limits.js';

export const CHUNK_SIZE = 16 * 1024;       // 16384
export const BACKPRESSURE_HIGH = 1_000_000; // pause sending above this
export const BACKPRESSURE_LOW = 256_000;    // resume at/below this

/**
 * Send a file in chunks over a DataChannel.
 *
 * @param {RTCDataChannel | {send:Function, bufferedAmount?:number, addEventListener?:Function}} channel
 * @param {File | Blob | {size:number, slice:Function, name?:string, type?:string}} file
 * @param {{
 *   transfer: { id: string, name?: string, size?: number, mime?: string, fromId: string, via?: 'webrtc' },
 *   onProgress?: (sent:number, total:number) => void,
 *   onState?: (s: 'pending'|'active'|'done'|'failed'|'cancelled'|'paused') => void,
 * }} ctx
 * @returns {{
 *   acknowledgeAccept: () => void,
 *   reject: (reason?: string) => void,
 *   pause: () => void,
 *   resume: () => void,
 *   cancel: () => void,
 *   done: Promise<{ id: string, bytesSent: number, checksum: string } | null>,
 * }}
 */
export function sendFile(channel, file, ctx) {
  const { transfer, onProgress = () => {}, onState = () => {} } = ctx;
  const { id, fromId } = transfer;
  if (!id || !fromId) throw new TypeError('sendFile: transfer.id and fromId required');
  const size = transfer.size ?? file.size ?? 0;
  const via = transfer.via ?? 'webrtc';

  // Up-front size check — refuse before any wire traffic.
  const cap = maxBytesFor(via);
  if (size > cap) {
    onState('failed');
    return {
      acknowledgeAccept() {},
      reject() {},
      pause() {},
      resume() {},
      cancel() {},
      done: Promise.reject(Object.assign(new Error('File exceeds transport size limit'), {
        code: 'SIZE_LIMIT',
      })),
    };
  }

  // ── control state ─────────────────────────────────────────────────────────
  let accepted = false;
  let rejected = false;
  let rejectReason = null;
  let paused = false;
  let cancelled = false;
  /** @type {Array<() => void>} resolvers waiting for accept/cancel/reject. */
  const acceptWaiters = [];
  /** @type {Array<() => void>} resolvers waiting for resume. */
  const resumeWaiters = [];

  const drainAccept = () => {
    while (acceptWaiters.length) acceptWaiters.shift()();
  };
  const drainResume = () => {
    while (resumeWaiters.length) resumeWaiters.shift()();
  };

  // ── send the offer ────────────────────────────────────────────────────────
  onState('pending');
  try {
    channel.send(
      serialize({
        kind: 'file-offer',
        id,
        name: transfer.name ?? file.name ?? 'file',
        size,
        mime: transfer.mime ?? file.type ?? 'application/octet-stream',
        fromId,
        ts: Date.now(),
      }),
    );
  } catch (err) {
    onState('failed');
    return failedHandle(err);
  }

  // ── backpressure helper ───────────────────────────────────────────────────
  // Resolve once the channel's send buffer drains below BACKPRESSURE_LOW.
  // We listen for 'bufferedamountlow' AND poll as a safety net: mobile Safari
  // (and some Chromium builds) don't fire 'bufferedamountlow' reliably, so an
  // event-only wait would hang the send loop forever — the exact "transfer
  // appears then freezes" symptom on phone→PC sends. The poll guarantees the
  // loop always resumes.
  const waitDrain = () =>
    new Promise((resolve) => {
      if ((channel.bufferedAmount ?? 0) <= BACKPRESSURE_LOW) {
        resolve();
        return;
      }
      let done = false;
      let poll = null;
      const finish = () => {
        if (done) return;
        done = true;
        try { channel.removeEventListener?.('bufferedamountlow', onLow); } catch { /* ignore */ }
        if (poll !== null) clearInterval(poll);
        resolve();
      };
      const onLow = () => finish();
      // Poll every 50ms regardless of the event — this is what unblocks mobile.
      poll = setInterval(() => {
        if ((channel.bufferedAmount ?? 0) <= BACKPRESSURE_LOW) finish();
      }, 50);
      try {
        channel.addEventListener?.('bufferedamountlow', onLow, { once: true });
      } catch {
        // No addEventListener (test mocks) — the poll handles it.
      }
    });

  // ── send loop ─────────────────────────────────────────────────────────────
  const run = async () => {
    // Await accept (caller invokes acknowledgeAccept on receiving file-accept).
    if (!accepted && !cancelled && !rejected) {
      await new Promise((resolve) => acceptWaiters.push(resolve));
    }
    if (rejected) {
      onState('failed');
      const e = new Error(rejectReason || 'Receiver declined');
      e.code = 'DECLINED';
      throw e;
    }
    if (cancelled) {
      onState('cancelled');
      return null;
    }

    onState('active');
    const crc = new Crc32Stream();
    let sent = 0;
    let seq = 0;

    while (sent < size) {
      if (cancelled) {
        try {
          channel.send(serialize({ kind: 'file-abort', id }));
        } catch { /* best effort */ }
        onState('cancelled');
        return null;
      }
      if (paused) {
        onState('paused');
        await new Promise((resolve) => resumeWaiters.push(resolve));
        if (cancelled) continue;
        onState('active');
      }

      // Pull this chunk's bytes.
      const end = Math.min(sent + CHUNK_SIZE, size);
      const slice = file.slice(sent, end);
      // file.slice returns a Blob; convert to ArrayBuffer.
      const buf = await blobToArrayBuffer(slice);
      const bytes = new Uint8Array(buf);
      crc.update(bytes);

      // Wait if buffered amount is over the high watermark.
      if ((channel.bufferedAmount ?? 0) > BACKPRESSURE_HIGH) {
        await waitDrain();
      }

      try {
        channel.send(
          serialize({ kind: 'file-chunk', id, seq, data: bytes }),
        );
      } catch (err) {
        onState('failed');
        throw err;
      }

      sent += bytes.length;
      seq += 1;
      onProgress(sent, size);
    }

    const checksum = crc.digest();
    try {
      channel.send(serialize({ kind: 'file-eof', id, checksum }));
    } catch (err) {
      onState('failed');
      throw err;
    }
    onState('done');
    return { id, bytesSent: sent, checksum };
  };

  const done = run().catch((err) => {
    if (!cancelled) onState('failed');
    throw err;
  });

  return {
    acknowledgeAccept() {
      if (accepted || rejected || cancelled) return;
      accepted = true;
      drainAccept();
    },
    reject(reason) {
      if (accepted || rejected || cancelled) return;
      rejected = true;
      rejectReason = reason || 'Receiver declined';
      drainAccept();
    },
    pause() {
      if (cancelled) return;
      if (paused) return;
      paused = true;
      try {
        channel.send(serialize({ kind: 'file-pause', id }));
      } catch { /* best effort */ }
    },
    resume() {
      if (!paused || cancelled) return;
      paused = false;
      try {
        channel.send(serialize({ kind: 'file-resume', id }));
      } catch { /* best effort */ }
      drainResume();
    },
    cancel() {
      if (cancelled) return;
      cancelled = true;
      try {
        channel.send(serialize({ kind: 'file-abort', id }));
      } catch { /* best effort */ }
      // Unblock any waiters so the loop can observe `cancelled`.
      drainAccept();
      drainResume();
    },
    done,
  };
}

function failedHandle(err) {
  return {
    acknowledgeAccept() {},
    reject() {},
    pause() {},
    resume() {},
    cancel() {},
    done: Promise.reject(err),
  };
}

function blobToArrayBuffer(blob) {
  // In modern browsers/Node 24 Blob has .arrayBuffer().
  if (blob && typeof blob.arrayBuffer === 'function') {
    return blob.arrayBuffer();
  }
  // Fallback for ancient environments.
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(fr.error);
    fr.readAsArrayBuffer(blob);
  });
}

// ── Receiver ────────────────────────────────────────────────────────────────

/**
 * Create a receiver context. The caller feeds parsed envelopes via `.feed(env)`.
 * On successful CRC verification, `onComplete({ id, blob, name, mime, size })`.
 *
 * @param {{
 *   onComplete?: (out: { id:string, blob: Blob, name: string, mime: string, size: number }) => void,
 *   onProgress?: (id: string, bytesReceived: number, totalSize: number) => void,
 *   onState?: (id: string, s: 'pending'|'active'|'done'|'failed'|'cancelled') => void,
 *   onOffer?: (offer: { id:string, name:string, size:number, mime:string, fromId:string }) => boolean | void,
 * }} [hooks]
 */
export function createReceiver(hooks = {}) {
  const {
    onComplete = () => {},
    onProgress = () => {},
    onState = () => {},
    onOffer = () => {},
  } = hooks;
  /** @type {Map<string, { meta:any, chunks: Uint8Array[], received:number, expectedSeq:number, crc:Crc32Stream }>} */
  const ctxs = new Map();

  return {
    /**
     * Feed an envelope into the receiver. Returns the receiver context if a
     * transfer just completed (or null otherwise) so callers can introspect.
     * @param {import('./types.js').Envelope} env
     */
    feed(env) {
      if (!env || typeof env.kind !== 'string') return;
      const id = env.id;
      switch (env.kind) {
        case 'file-offer': {
          if (ctxs.has(id)) return; // dedupe handled higher up
          // hook may return false to auto-decline
          const accepted = onOffer({
            id,
            name: env.name,
            size: env.size,
            mime: env.mime,
            fromId: env.fromId,
          });
          if (accepted === false) return;
          ctxs.set(id, {
            meta: { name: env.name, size: env.size, mime: env.mime, fromId: env.fromId },
            chunks: [],
            received: 0,
            expectedSeq: 0,
            crc: new Crc32Stream(),
            // Optional streaming sink (set via attachSink before chunks flow).
            // When present, chunks write straight to it (disk) instead of
            // accumulating in `chunks` — so huge files don't buffer in RAM.
            sink: null,
            writeChain: Promise.resolve(),
          });
          onState(id, 'pending');
          return;
        }
        case 'file-chunk': {
          const ctx = ctxs.get(id);
          if (!ctx) return;
          if (env.seq !== ctx.expectedSeq) {
            // Defense per F11.1 test plan — drop and surface failed.
            ctxs.delete(id);
            onState(id, 'failed');
            return;
          }
          const bytes = env.data instanceof Uint8Array
            ? env.data
            : new Uint8Array(env.data);
          ctx.received += bytes.length;
          ctx.expectedSeq += 1;
          ctx.crc.update(bytes);
          if (ctx.sink) {
            // Stream straight to the sink (e.g. a file on disk). Chain writes
            // so order is preserved and EOF can await them all. Never buffer.
            ctx.writeChain = ctx.writeChain.then(() => ctx.sink.write(bytes));
          } else {
            ctx.chunks.push(bytes);
          }
          if (ctx.received === bytes.length) onState(id, 'active');
          onProgress(id, ctx.received, ctx.meta.size);
          return;
        }
        case 'file-eof': {
          const ctx = ctxs.get(id);
          if (!ctx) return;
          const expected = (env.checksum || '').toLowerCase();
          const actual = ctx.crc.digest();
          if (expected !== actual) {
            if (ctx.sink) {
              ctx.writeChain
                .then(() => ctx.sink.abort && ctx.sink.abort())
                .catch(() => {});
            }
            ctxs.delete(id);
            onState(id, 'failed');
            return;
          }
          if (ctx.sink) {
            // Streamed to disk: flush the write chain, close the sink, then
            // report complete WITHOUT a blob (the file is already saved).
            const { sink, writeChain, meta } = ctx;
            ctxs.delete(id);
            writeChain
              .then(() => sink.close())
              .then(() => {
                onState(id, 'done');
                onComplete({
                  id,
                  blob: null,
                  streamed: true,
                  name: meta.name,
                  mime: meta.mime,
                  size: meta.size,
                });
              })
              .catch(() => onState(id, 'failed'));
            return;
          }
          const blob = new Blob(ctx.chunks, { type: ctx.meta.mime || 'application/octet-stream' });
          ctxs.delete(id);
          onState(id, 'done');
          onComplete({
            id,
            blob,
            streamed: false,
            name: ctx.meta.name,
            mime: ctx.meta.mime,
            size: ctx.meta.size,
          });
          return;
        }
        case 'file-abort': {
          const ctx = ctxs.get(id);
          if (!ctx) return;
          if (ctx.sink) {
            ctx.writeChain
              .then(() => ctx.sink.abort && ctx.sink.abort())
              .catch(() => {});
          }
          ctxs.delete(id);
          onState(id, 'cancelled');
          return;
        }
        case 'file-pause':
        case 'file-resume':
          // Receiver doesn't need to do anything special; the sender drives
          // the loop. We surface state for UI hooks.
          if (ctxs.has(id)) onState(id, env.kind === 'file-pause' ? 'pending' : 'active');
          return;
        default:
          // Other envelope kinds (code, ping, etc.) aren't our concern.
          return;
      }
    },
    /**
     * Attach a streaming sink for transfer `id` so its chunks write straight
     * through (e.g. to disk via the File System Access API) instead of
     * buffering in memory. Must be called BEFORE chunks arrive (the receiver
     * does this on accept, before sending file-accept). sink = { write(bytes),
     * close(), abort? }. Returns false if the transfer context isn't known yet.
     */
    attachSink(id, sink) {
      const ctx = ctxs.get(id);
      if (!ctx || !sink || typeof sink.write !== 'function') return false;
      ctx.sink = sink;
      ctx.writeChain = Promise.resolve();
      return true;
    },
    /** Test/debug accessor — peek at the in-flight contexts. */
    _ctxs: ctxs,
  };
}

/**
 * Constants used by tests; pinning the numbers here gives one place to audit.
 */
export const __test__ = { CHUNK_SIZE, BACKPRESSURE_HIGH, BACKPRESSURE_LOW, LIMITS };
